import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import "./TermsAndConditionsModal.css";

interface PrivacyPolicyModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const PrivacyPolicyModal: React.FC<PrivacyPolicyModalProps> = ({ isOpen, onClose }) => {
  useEffect(() => {
    const handleEscapeKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    if (isOpen) {
      document.body.style.overflow = "hidden";
      document.addEventListener("keydown", handleEscapeKey);
    }

    return () => {
      document.removeEventListener("keydown", handleEscapeKey);
      document.body.style.overflow = "unset";
    };
  }, [isOpen, onClose]);

  if (!isOpen) {
    return null;
  }

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleCloseClick = () => {
    onClose();
  };

  const modalContent = (
    <div className="terms-modal-backdrop" onClick={handleBackdropClick}>
      <div className="terms-modal-container">
        <div className="terms-modal-header">
          <h2>Privacy Policy</h2>
          <button className="terms-modal-close" onClick={handleCloseClick} type="button">
            ×
          </button>
        </div>

        <div className="terms-modal-content">
          <div className="terms-content">
            <h3>Gymea Edu - Privacy Policy</h3>
            <p><strong>Effective Date: 8 October 2025</strong></p>

            <h3>1. Introduction</h3>
            <p>
              Gymea Edu respects your privacy and is committed to protecting personal information in accordance with the Privacy Act 1988 (Cth) and relevant state or territory privacy laws.
            </p>
            <p>
              This Privacy Policy explains how we collect, use, and protect information entered into the Gymea Edu website. By using Gymea Edu, you agree to this policy.
            </p>

            <h3>2. What Information We Collect</h3>
            <p>Gymea Edu is designed to minimise the collection and retention of personal information. We collect only what is necessary to provide our services.</p>
            <p><strong>Teacher Input:</strong> You may input limited educational, behavioural, or contextual information to generate learning plans, reports, or correspondence.</p>
            <p><strong>Student Names in Reports:</strong> First names may be included in student report comments for drafting purposes.</p>
            <p>These first names are never stored or retained, and never shared with any third party.</p>
            <p><strong>No Persistent Data:</strong> Gymea Edu may temporarily store non-identifiable user-generated documents (for example, anonymised drafts or templates) to improve functionality and the user experience. No documents containing identifying information are stored or shared with third parties.</p>
            <p><strong>No Automatic Collection:</strong> We do not collect or track identifying information about students, staff, or families.</p>
            <p>
              <strong>Misuse of Gymea Services:</strong> If identifying or sensitive information (such as full names, addresses, or dates of birth) is entered contrary to our instructions, it is considered user error, and the user remains responsible for that disclosure.
            </p>
            <p><strong>We never ask for or store:</strong> Full student names, surnames, or identifying details.</p>

            <h3>3. Sensitive or Identifiable Information</h3>
            <p>Gymea Edu does not require any personal, sensitive, or health-related information to operate.</p>
            <p>
              Educators may include de-identified educational, behavioural, or medical context (for example, noting that a student has ADHD or other learning needs) when creating personalised learning materials to improve their quality and relevance.
            </p>
            <p>Gymea Edu never requests personally identifying details, and ensuring that such information is not provided remains the responsibility of the user.</p>

            <h3>4. How We Use Information</h3>
            <p>Any information you enter is used solely to generate draft educational materials (e.g., reports, learning plans, or letters).</p>
            <p>No personal information is stored, shared, or used for advertising, or third-party integration.</p>

            <h3>5. Data Hosting and Storage Location</h3>
            <p>Gymea Edu infrastructure is hosted on Amazon Web Services (AWS) servers located in Sydney, Australia.</p>
            <p>
              Certain non-identifiable, aggregated, or anonymised data may be processed by third-party services (for example, analytics or text generation).
            </p>

            <h3>6. Data Security</h3>
            <p>Gymea Edu takes reasonable steps to secure its systems against unauthorised access or misuse.</p>
            <p>Because no online service can be completely secure, we cannot guarantee absolute protection.</p>

            <h3>7. Third Parties</h3>
            <p>
              Gymea Edu does not sell, share, or transfer user data to third parties. We may use trusted service providers for hosting and infrastructure (such as AWS), but no identifying information or user-generated educational content is transmitted to them.
            </p>

            <h3>8. Your Responsibilities</h3>
            <ul>
              <li>Ensure that all information entered into Gymea Edu is de-identified in line with your school or system's data protection policies.</li>
              <li>Avoid inputting personally identifiable information or sensitive data beyond what is strictly necessary for draft creation.</li>
              <li>Review, edit, and verify all generated content before using it in any professional or official context.</li>
            </ul>

            <h3>9. Cookies and Analytics</h3>
            <p>Gymea Edu may use minimal cookies or analytics tools to monitor site performance and usage patterns. These do not capture or track students' personal information.</p>

            <h3>10. Your Rights</h3>
            <p>You have the right to contact us at any time to:</p>
            <ul>
              <li>Ask questions about this Privacy Policy</li>
              <li>Request clarification on data handling</li>
            </ul>
            <p>Since Gymea Edu does not store identifiable user input, we cannot retrieve or delete user-submitted data after a session ends.</p>

            <h3>11. Changes to This Policy</h3>
            <p>
              We may update this Privacy Policy from time to time. Any changes will take effect immediately upon posting on the Website. Continued use of Gymea Edu indicates your acceptance of the updated policy.
            </p>

            <h3>12. Contact Us</h3>
            <p>
              If you have questions or concerns about this Privacy Policy, please <a className="terms-contact-link" href="https://docs.google.com/forms/d/e/1FAIpQLSeb137Z79HtvYCeb59ppvQq8q9q76_AjHmd4tbi-GlUPAIGnw/viewform" target="_blank" rel="noopener noreferrer">contact us here</a>.
            </p>
          </div>
        </div>

        <div className="terms-modal-footer">
          <button className="terms-modal-close-button" onClick={handleCloseClick} type="button">
            Close
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};
